import { FC, FormEvent, useEffect, useRef, useState } from "react";
import KickImage from 'Reuseables/KickImage';
import styled from "styled-components";

//Constants
import { FontFamilyBold, FontFamilyRegular } from "../../../constants";
import { useOutsideAlerter } from "../../../helpers/dom";
import { capitalizeSentence } from "../../../utils/Helpers";

//Assets
import DropdownIcon from "../../../images/dropdown-icon.png";

// Define Interface
type Props = {
  className?: string;
  position?: string;
  title?: string;
  placeholder?: string;
  value?: string;
  name?: string;
  autocomplete?: string;
  min?: string;
  options: { value: string, text: string }[];
  onChange: (e: FormEvent<HTMLInputElement>) => void;
  onSelect: (e?: any) => void;
}

interface StyledCompProps {
  fontFamilyBold?: string;
  fontFamilyRegular?: string;
}

const StyledComponent = styled.div<StyledCompProps>`
  width: 100%;
  .sws-title{
    font-family: ${props => props.fontFamilyRegular};
    font-size: 12px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    text-align: left;
    color: #242424;
    margin-bottom: 8px;
  }
  .dropdown{
    width: 100%;
    padding: 16px;
    border-radius: 8px;
    border: solid 1px #ebebeb;
    background-color: #fff;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    cursor: pointer;
    .search-input{
      border: none;
      width: 100%;
      background: #fff;
    }
    input{
      &::placeholder {
        font-family: ${props => props.fontFamilyRegular};
        font-size: 12px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.5;
        letter-spacing: normal;
        text-align: left;
        color: #bebebe;
      }
      
      &:focus-visible{
        outline: 0;
      }
    }

    //remove arrow of type number
    /* Chrome, Safari, Edge, Opera */
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    /* Firefox */
    input[type=number] {
        -moz-appearance: textfield;
    }

    .dropdown-wrapper{
      width: 100%;
      background-color: #fff;
      position: absolute;
      left: 0;
      bottom: 49px;
      display: none;
      z-index: 1;
      .scroll-wrapper{
        overflow-y: auto;
        width: 100%;
        border-radius: 8px;
        border: solid 1px #ebebeb;
        background-color: #fff;
        max-height: 255px;
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.16);
      }
      .dropdown-item{
        padding: 16px;
        border-bottom: solid 1px #ebebeb;
        font-family: ${props => props.fontFamilyRegular};
        font-size: 12px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.5;
        letter-spacing: normal;
        text-align: left;
        color: #242424;
        &:last-child{
          border-bottom: unset;
        }
        &:active, &:focus, &:focus-visible{
          background: #fff;
          outline: 0;
        }
      }

      &.position-bottom{
        top: 49px;
      }
      &.position-top{
        bottom: 49px;
      }
      &.show{
        display: block;
      }
    }
    .arrow-icon{
      width: 10px;
      height: 6px;
      object-fit: contain;
    }
  }
`
StyledComponent.defaultProps = {
  fontFamilyBold: FontFamilyBold,
  fontFamilyRegular: FontFamilyRegular
}


const CustomerSelectWithSearch: FC<Props> = ({
  position,
  options,
  value,
  name,
  onChange,
  onSelect,
  placeholder,
  className,
  autocomplete,
  min,
  title
}) => {
  const datalistRef = useRef(null)
  const [showAllList, setShowAllList] = useState(false);
  const [focusedInput, setFocusedInput] = useState(false)
  const [newOptions, setOptions] = useState<any>([...options])
  const onFocused = () => newOptions.length && setFocusedInput(true)
  const onBlur = () => { setFocusedInput(false) }

  useOutsideAlerter(datalistRef, () => { onBlur() })

  //Handlers
  const onChangeHandler = (e) => {
    const { value } = e.target;

    if (!value) {
      setOptions([...options])
    } else {
      const tempOptions = [...options];
      const filteredOptions = tempOptions?.filter(opt => {
        if (!opt?.value || typeof opt?.value !== "string") return false;

        const _value = opt.value.toLowerCase()
        if (!_value.includes(value.toLowerCase())) return false

        return true
      });

      setOptions(filteredOptions)
    }

    onFocused()
    onChange(e)
  }

  const onSelectHandler = (value) => {
    setOptions([...options])
    onSelect({ name, value })
    onBlur()
  }

  useEffect(() => {
    setOptions([...options])
  }, [options])

  return (
    <StyledComponent className={className}>
      {title ? <p className="sws-title">{title}</p> : null}
      <div ref={datalistRef} className="dropdown">
        <input
          onFocus={onFocused}
          value={capitalizeSentence(value)}
          onChange={onChangeHandler}
          placeholder={placeholder}
          className="search-input"
          name={name}
          autoComplete={autocomplete}
          min={min}
        />
        <div className={`dropdown-wrapper position-${position} ${focusedInput ? "show" : ""}`}>
          <div className="scroll-wrapper">
            {
              newOptions?.length ? newOptions?.map((option, key) => {
                return (
                  <div
                    onClick={() => onSelectHandler(option)}
                    key={key}
                    className="dropdown-item"
                  >
                    <span>{capitalizeSentence(option.text)}</span>
                  </div>
                )
              }) : null
            }
          </div>
        </div>
        <KickImage onClick={onFocused} src={DropdownIcon} className="arrow-icon" alt="dropdown" />
      </div>
    </StyledComponent>
  )
}
CustomerSelectWithSearch.defaultProps = {
  className: "",
  title: "",
  placeholder: "",
  options: [{ value: "", text: "" }],
  value: "",
  name: "",
  position: "bottom",
  autocomplete: "off",
  min: "0",
  onChange: () => { },
  onSelect: () => { }
}

export default CustomerSelectWithSearch